// Sound fading feature to a certain volume
// Sound Instance and Audio Sprite loading and playing
// Custom sound states

// This class provides a unified sound creation and usage experience
import {
    AudioSprite,
    Sound,
    SoundLoadConfig,
    AudioSpriteLoadConfig,
    SoundKey,
    AudioSpriteKey,
    SoundInstance,
    AudioSpriteInstance,
    SoundPlayOptions,
    AudioSpritePlayOptions,
    ISoundInstance,
} from "./SoundManagerUtils";

class SoundManager {
    private static _instance: SoundManager;

    private _sounds: Map<SoundKey, Sound> = new Map<SoundKey, Sound>();
    private _audioSpriteSounds: Map<SoundKey, AudioSprite> = new Map<
        SoundKey,
        AudioSprite
    >();
    private _soundInstances: ISoundInstance[] = [];

    // User-side mute
    private _isGameMuted: boolean = false;
    // Tab focus/unfocus mute
    private _isTabMuted: boolean = false;
    private get _isEffectivelyMuted(): boolean {
        return this._isGameMuted || this._isTabMuted;
    }
    private _masterVolume: number = 1;

    private constructor() {
        document.addEventListener("visibilitychange", () => {
            this._isTabMuted = document.hidden;
            this._computeMasterVolume();
        });
    }

    public static getInstance(): SoundManager {
        if (!this._instance) {
            this._instance = new SoundManager();
        }

        return this._instance;
    }

    public setGameMuted(isMuted: boolean) {
        this._isGameMuted = isMuted;
        this._computeMasterVolume();
    }

    public async createSound(config: SoundLoadConfig): Promise<void> {
        const sound: Sound = new Sound();
        await sound.load(config);
        this._sounds.set(config.key, sound);
    }

    public async createAudioSprite(config: AudioSpriteLoadConfig): Promise<void> {
        const audioSprite: AudioSprite = new AudioSprite();
        await audioSprite.load(config);
        this._audioSpriteSounds.set(config.key, audioSprite);
    }

    // An ISoundInstance is created after playing an ISound object

    // The play methods play and store the instances they play internally
    public async playSound(
        key: SoundKey,
        playOptions?: SoundPlayOptions,
    ): Promise<SoundInstance | undefined> {
        const sound: Sound | undefined = this._tryGetSound(key);
        if (!sound) return Promise.resolve(undefined);

        let soundInstanceGeneric: ISoundInstance | undefined = await sound.play(playOptions);

        if (!soundInstanceGeneric) {
            return undefined;
        }

        const soundInstance: SoundInstance = soundInstanceGeneric as SoundInstance;

        this._soundInstances.push(soundInstance);

        soundInstance.tryGetMediaInstance()?.on('end', () => {
            const index: number = this._soundInstances.findIndex(instance => instance.id === soundInstance.id);
            if (index >= 0) {
                this._soundInstances.splice(index, 1);
            }
        });

        return soundInstance;
    }

    public async playAudioSprite(
        key: AudioSpriteKey,
        playOptions: AudioSpritePlayOptions,
    ): Promise<AudioSpriteInstance | undefined> {
        const audioSprite: AudioSprite | undefined =
            this._tryGetAudioSprite(key);
        if (!audioSprite) return Promise.resolve(undefined);

        let audioSpriteInstanceGeneric: ISoundInstance | undefined = await audioSprite.play(playOptions);

        if (!audioSpriteInstanceGeneric) {
            return undefined;
        }

        const audioSpriteInstance: AudioSpriteInstance = audioSpriteInstanceGeneric as AudioSpriteInstance;

        this._soundInstances.push(audioSpriteInstance);

        audioSpriteInstance.tryGetMediaInstance()?.on('end', () => {
            const index: number = this._soundInstances.findIndex(instance => instance.id === audioSpriteInstance.id);
            if (index >= 0) {
                this._soundInstances.splice(index, 1);
            }
        });

        return audioSpriteInstance;
    }

    public stopSoundInstance(soundInstance: ISoundInstance): void {
        if (!soundInstance) return;

        soundInstance.stop();
        soundInstance.reset();

        const index: number = this._soundInstances.findIndex(instance => instance.id === soundInstance.id);
        if (index >= 0) {
            this._soundInstances.splice(index, 1);
        }
    }

    public pauseSoundInstance(soundInstance: ISoundInstance): void {
        if (!soundInstance) return;

        soundInstance.pause();
    }

    public resumeSoundInstance(soundInstance: ISoundInstance): void {
        if (!soundInstance) return;

        soundInstance.resume();
    }

    public setSoundInstanceVolume(soundInstance: ISoundInstance, volume: number, fadeDuration: number = 0.25, fadeFinished?: () => void) {
        if (!soundInstance) return;

        soundInstance.setVolume(volume * this._masterVolume, fadeDuration, fadeFinished);
    }

    public setMuted(mute: boolean = true): void {
        // Change the mute state of the future instances
        for (const [_, sound] of this._sounds) {
            sound.setMuted(mute);
        }
        for (const [_, sound] of this._audioSpriteSounds) {
            sound.setMuted(mute);
        }

        // Change the mute state of present sound instances
        for (const instance of this._soundInstances) {
            instance.setMuted(mute);
        }
    }

    private _tryGetSound(key: SoundKey): Sound | undefined {
        const sound: Sound | undefined = this._sounds.get(key);
        if (!sound) {
            console.warn(`Sound ${key} wasn't loaded. Can not play the sound.`);
            return undefined;
        }

        return sound;
    }

    private _tryGetAudioSprite(
        key: AudioSpriteKey,
    ): AudioSprite | undefined {
        const audioSprite: AudioSprite | undefined =
            this._audioSpriteSounds.get(key);
        if (!audioSprite) {
            console.warn(
                `Audio Sprite ${key} wasn't loaded. Can not play the sound.`,
            );
            return undefined;
        }

        return audioSprite;
    }

    private _computeMasterVolume() {
        const newVolume = this._isEffectivelyMuted ? 0 : 1;
        if (this._masterVolume !== newVolume) {
            this._masterVolume = newVolume;
            for (const soundInstance of this._soundInstances) {
                soundInstance.setVolume(
                    soundInstance.getBaseVolume() * this._masterVolume,
                );
            }
        }
    }
}

export const soundManager = SoundManager.getInstance();
